// lib/calendarActions.mjs — R115 guest: the pure rules behind the Calendar's
// reservation / listing actions (B1 Resolution, B2 Recovery, B3 Claim,
// B4 Special-offer flag, B5 Message, B7 listing jumps, B8 ★ score). Pure ESM,
// no DOM, no I/O: client/src/pages/calendar/guest/* imports it and
// `node lib/calendarActions.test.mjs` tests the SAME code.

// ── channels ────────────────────────────────────────────────────────────
// Hostaway channel_name → the Resolutions ledger's channel vocabulary
// (server.js RES_CHANNELS = airbnb | vrbo | booking | direct | other).
// Live raw values: airbnbOfficial, homeaway, bookingcom, direct,
// bookingengine, customIcal.
export const RES_CHANNELS = ['airbnb', 'vrbo', 'booking', 'direct', 'other'];
export const RES_CHANNEL_LABEL = { airbnb: 'Airbnb', vrbo: 'VRBO', booking: 'Booking.com', direct: 'Direct', other: 'Other' };
export function resChannelOf(channel) {
  const c = String(channel == null ? '' : channel).trim().toLowerCase();
  if (!c) return 'other';
  if (c.includes('airbnb')) return 'airbnb';
  if (c.includes('homeaway') || c.includes('vrbo')) return 'vrbo';
  if (c.includes('booking.com') || c === 'bookingcom' || c === 'booking') return 'booking';
  if (['direct', 'bookingengine', 'website', 'manual', 'hostaway'].includes(c)) return 'direct';
  return 'other';
}
export const isAirbnbChannel = (channel) => resChannelOf(channel) === 'airbnb';

// ── which stay actions apply ────────────────────────────────────────────
// Owner stays: none (nothing to message / refund / claim / recover).
// Inquiries: Message only (not booked — no refund, damage or recovery).
// Stays (upcoming / in house / checked out — incl. a cancelled one opened
// from a link: post-stay refunds and claims still happen): all four.
export function stayActions(stay) {
  const kind = stay && stay.kind;
  if (kind === 'owner' || !stay) return { message: false, recovery: false, claim: false, resolution: false };
  if (kind === 'inquiry') return { message: true, recovery: false, claim: false, resolution: false };
  return { message: true, recovery: true, claim: true, resolution: true };
}

// ── claims ──────────────────────────────────────────────────────────────
export function slugify(s, max = 40) {
  return String(s == null ? '' : s).toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, max).replace(/-+$/g, '') || 'claim';
}
// The idempotency key the server dedupes on: the same damage on the same stay
// (or the same listing on the same day) is ONE claim; a different title is another.
// R115 review: a 24-char readable slug + a hash of the WHOLE normalised title — a 40-char cut made
// "…closet door hinge" / "…closet door handle" one claim, and every non-Latin title "claim".
export const normTitle = (s) => String(s == null ? '' : s).normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
export function h32(s) {   // FNV-1a 32-bit → 8 hex
  let h = 0x811c9dc5;
  const t = String(s);
  for (let i = 0; i < t.length; i++) { h ^= t.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
}
export const claimSourceRef = (hid, title) => `res:${String(hid)}:${slugify(title, 24)}:${h32(normTitle(title))}`;
export const listingClaimSourceRef = (lid, title, day) => `lst:${String(lid)}:${slugify(title, 24)}:${h32(normTitle(title))}:${String(day || '')}`;
const num = (v) => { const t = String(v == null ? '' : v).replace(/[$,\s]/g, ''); return t === '' ? NaN : Number(t); };
// form: { title, note, amount, guest } + ctx: { listing_id, listing_name, reservation_id?, day?, by? }
export function claimBody(form, ctx) {
  const f = form || {}, c = ctx || {};
  const title = String(f.title || '').trim();
  if (!title) return { ok: false, error: 'Give the claim a title — what was damaged?' };
  if (!c.listing_id) return { ok: false, error: 'No property on this claim.' };
  let amount;
  if (String(f.amount == null ? '' : f.amount).trim()) {
    amount = num(f.amount);
    if (!Number.isFinite(amount) || amount < 0) return { ok: false, error: 'Amount must be a positive number (or leave it blank).' };
    if (amount === 0) amount = undefined;
  }
  const body = {
    listing_id: String(c.listing_id), listing_name: String(c.listing_name || ''), title: title.slice(0, 200),
    note: String(f.note || '').trim().slice(0, 4000) || undefined, amount, guest: String(f.guest || '').trim().slice(0, 120) || undefined,
    source: 'calendar',
    source_ref: c.reservation_id ? claimSourceRef(c.reservation_id, title) : listingClaimSourceRef(c.listing_id, title, c.day),
    reservation_id: c.reservation_id ? String(c.reservation_id) : undefined,
    by: c.by || undefined,
  };
  return { ok: true, body };
}

// ── resolutions ─────────────────────────────────────────────────────────
export const RES_REASONS = [['eci_lco', 'ECI / LCO'], ['issue', 'Issue'], ['pet_fee', 'Pet fee'], ['pool_heating', 'Pool heating'], ['damage', 'Damage'], ['other', 'Other']];
// http/https only (the server enforces the same — ResolutionsPage.safeLink): '' = none, null = invalid
export function safeLink(v) {
  const s = String(v == null ? '' : v).trim();
  if (!s) return '';
  if (s.length > 2000 || /\s/.test(s)) return null;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s}`);
    return (u.protocol === 'https:' || u.protocol === 'http:') && u.hostname.includes('.') ? u.href : null;
  } catch { return null; }
}
// form: { direction, amount, reason_code, reason, mode, owner_pct, channel, guest, link, date }
// ctx:  { listing_id, listing_name, owner_id?, reservation_id?, confirmation_code?, check_in?, check_out?, fmt? }
// ctx.client_ref (R115 review): the sheet's idempotency key, minted once when it opens
export function resolutionBody(form, ctx) {
  const f = form || {}, c = ctx || {};
  const direction = f.direction === 'charge' ? 'charge' : (f.direction === 'refund' ? 'refund' : null);
  if (!direction) return { ok: false, error: 'Pick Refund or Extra charge.' };
  const amount = num(f.amount);
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, error: 'Enter a positive amount.' };
  const mode = ['us', 'owner', 'split'].includes(f.mode) ? f.mode : 'us';
  let owner_pct = null;
  if (mode === 'split') {
    owner_pct = num(f.owner_pct);
    if (!Number.isFinite(owner_pct) || owner_pct < 0 || owner_pct > 100) return { ok: false, error: "The owner's share must be 0–100%." };
  }
  const link = safeLink(f.link);
  if (link === null) return { ok: false, error: 'The link should be a web address starting with https:// — fix it or clear it.' };
  if (!c.listing_id) return { ok: false, error: 'No property on this resolution.' };
  const reason_code = RES_REASONS.some(([k]) => k === f.reason_code) ? f.reason_code : 'other';
  const md = typeof c.fmt === 'function' ? c.fmt : (d) => d;
  const body = {
    source: 'calendar',
    date: /^\d{4}-\d{2}-\d{2}$/.test(String(f.date || '')) ? f.date : undefined,
    listing_id: String(c.listing_id), listing_name: String(c.listing_name || ''),
    owner_id: mode !== 'us' && c.owner_id ? String(c.owner_id) : null,
    guest: String(f.guest || '').trim().slice(0, 120),
    channel: RES_CHANNELS.includes(f.channel) ? f.channel : 'other',
    direction, amount: Math.round(amount * 100) / 100,
    reason: String(f.reason || '').trim().slice(0, 300),
    reason_code, taxable: 'auto', commissionable: 'auto', link,
    attribution: { mode, owner_pct },
  };
  if (c.client_ref && /^[\w-]{8,64}$/.test(String(c.client_ref))) body.client_ref = String(c.client_ref);
  if (c.reservation_id) {
    body.reservation_id = String(c.reservation_id);
    if (c.confirmation_code) body.confirmation_code = String(c.confirmation_code).slice(0, 20);
    if (c.check_in && c.check_out) body.note = `Calendar · stay ${md(c.check_in)} → ${md(c.check_out)}`;
  }
  return { ok: true, body };
}

// ── special offers (B4) ─────────────────────────────────────────────────
// (the Special Offers page's COMP_LABEL vocabulary)
export const COMPONENT_LABEL = { baseRate: 'base rate', cleaningFee: 'cleaning fee', managementFeeAirbnb: 'Airbnb management fee', occupancyTax: 'occupancy tax', lodgingTax: 'lodging tax', cityTax: 'city tax', petFee: 'pet fee', linenFeeAirbnb: 'linen fee', resortFeeAirbnb: 'resort fee', pricePerExtraPerson: 'extra guest fee' };
export const missingText = (missing) => (Array.isArray(missing) ? missing : []).map(m => COMPONENT_LABEL[m] || String(m)).join(', ');
// The Special Offers API gate (server/specialOffers.js SO_ROLES) minus accounting
// (which never reaches the calendar) — remote_ops would land on a 🔒 tab.
export const SO_ROLES = ['admin', 'director', 'manager'];
export const canOpenSpecialOffers = (role) => SO_ROLES.includes(String(role || '')) || role === 'dev';
// state → what the stay shows. null = nothing (ok / not Airbnb).
export function soMark(so) {
  if (!so || !so.state) return null;
  const miss = missingText(so.missing);
  switch (so.state) {
    case 'flagged': return { tone: 'warn', pill: 'pill-amber', glyph: '⚑', text: 'Special offer', title: `Special offer — fees not broken out${miss ? ` (missing: ${miss})` : ''}. Accounting splits it on the Special Offers page.`, bar: true };
    case 'recorded': return { tone: 'muted', pill: 'pill-gray', glyph: '⚑', text: 'Special offer · fees recorded', title: 'Special offer — accounting recorded the fee split (not yet sent to Hostaway).', bar: true };
    case 'resolved': return { tone: 'muted', pill: 'pill-gray', glyph: '', text: 'Special offer · OK by note', title: 'Flagged as a special offer, then marked fine as it is (with a note) on the Special Offers page.', bar: false };
    case 'unchecked': return { tone: 'muted', pill: 'pill-gray', glyph: '', text: 'Fees not checked yet', title: "This Airbnb booking's fee breakdown hasn't been read from Hostaway yet — the nightly check covers the next 30 days of check-ins.", bar: false };
    case 'error': return { tone: 'muted', pill: 'pill-gray', glyph: '', text: 'Fee check failed', title: "Hostaway didn't answer the last fee check for this booking.", bar: false };
    default: return null;
  }
}

// ── review score (B8) ───────────────────────────────────────────────────
// The row ★ = AIRBNB-only average with at least minN ratings (lib/reviewScores
// badgeOf — never Hostaway's all-channel number passed off as "Airbnb").
export function scoreBadge(scores, minN = 3) {
  const a = scores && scores.airbnb;
  if (!a || a.avg == null || !(a.n >= minN)) return null;
  const tone = a.avg >= 4.8 ? 'ok' : a.avg >= 4.5 ? 'muted' : 'warn';
  return { text: Number(a.avg).toFixed(2), title: `Airbnb ${Number(a.avg).toFixed(2)} · ${a.n} review${a.n === 1 ? '' : 's'}${scoreSecondary(scores) ? ` · ${scoreSecondary(scores)}` : ''}`, tone };
}
export function scoreMain(scores) {
  const a = scores && scores.airbnb;
  if (!a || a.avg == null || !a.n) return null;
  return { avg: Number(a.avg).toFixed(2), n: a.n, text: `★ ${Number(a.avg).toFixed(2)} Airbnb · ${a.n} review${a.n === 1 ? '' : 's'}` };
}
export function scoreSecondary(scores) {
  if (!scores) return '';
  const parts = [];
  if (scores.all && scores.all.avg != null && scores.all.n) parts.push(`all channels ${Number(scores.all.avg).toFixed(2)} (${scores.all.n})`);
  if (scores.hostaway != null) parts.push(`Hostaway ${Number(scores.hostaway).toFixed(2)}`);
  return parts.join(' · ');
}

// ── messages (B5) ───────────────────────────────────────────────────────
// The sheet shows the latest `limit` messages, oldest → newest (chat order).
export function threadView(messages, limit = 30) {
  const list = (Array.isArray(messages) ? messages : []).filter(m => m && (m.body || (m.images && m.images.length) || m.text));
  const sorted = list.slice().sort((a, b) => String(a.at || a.created_at || '').localeCompare(String(b.at || b.created_at || '')));
  const shown = sorted.slice(-limit);
  return { shown, older: sorted.length - shown.length };
}
export const PMS_BODY_MAX = 2000;
export const SMS_BODY_MAX = 1600;
// What the composer may do on one channel. pms: { cid, sendOn } · sms: { line, to }.
export function composerState(channel, o = {}) {
  if (channel === 'pms') {
    if (!o.cid) return { canType: false, canSend: false, max: PMS_BODY_MAX, reason: 'No PMS conversation for this booking.' };
    if (!o.sendOn) return { canType: false, canSend: false, max: PMS_BODY_MAX, reason: 'PMS replies are switched off on this server (MESSAGING_PMS_SEND). Read here; reply in Hostaway (or by SMS) until the flag is on.' };
    return { canType: true, canSend: true, max: PMS_BODY_MAX, confirm: true, reason: '' };
  }
  if (channel === 'sms') {
    if (!o.line || !o.to) return { canType: false, canSend: false, max: SMS_BODY_MAX, reason: 'No SMS line has a thread with this guest.' };
    return { canType: true, canSend: true, max: SMS_BODY_MAX, confirm: false, reason: '' };
  }
  return { canType: false, canSend: false, max: 0, reason: '' };
}
// A failed send: did it maybe go out anyway? (QuoInboxPage.maybeSent: a
// timeout / 5xx / network error might have; a definite 4xx never did.)
export function maybeSent(status, body) {
  if (body && body.may_have_sent === true) return true;
  if (body && body.may_have_sent === false) return false;
  return !status || status >= 500;
}

// ── listing jumps (B7) ──────────────────────────────────────────────────
export const OPTIMIZER_ROLES = ['admin', 'director'];
export const canOpenOptimizer = (role) => OPTIMIZER_ROLES.includes(String(role || '')) || role === 'dev';
// An external link the page may render as an href: https + an allowed host.
export function safeExternal(url, hostRe) {
  const s = String(url == null ? '' : url).trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    if (u.protocol !== 'https:') return null;
    return !hostRe || hostRe.test(u.hostname) ? u.href : null;
  } catch { return null; }
}
export const HOSTAWAY_HOST_RE = /(^|\.)hostaway\.com$/i;
export const AIRBNB_HOST_RE = /(^|\.)airbnb\.[a-z.]{2,10}$/i;
